import { FC } from 'react';
import { Badge, Button } from 'reactstrap';
import ApplicantRowHeader from './ApplicantRowHeader';
import ApplicantRow, { ApplicantRowData } from './ApplicantRow';
import BulkActionBar from './BulkActionBar';
import IntakeDropzone from './IntakeDropzone';
import { intersectWithVisible } from './selection.util';

interface StatusCounts {
  newCount: number;
  sendOutreach: number;
  needsHuman: number;
  assigned: number;
  declined: number;
}

export interface JobOrderBlockData {
  jobOrderId: string;
  jobId: number;
  jobTitle: string | null | undefined;
  city: string | null | undefined;
  stateCode: string | null | undefined;
  aiRecruiterEnabled: boolean;
  applicantCount: number;
  matchCount: number;
  statusCounts: StatusCounts;
  applicants: ApplicantRowData[];
}

interface Props {
  companyId: string;
  data: JobOrderBlockData;
  isExpanded: boolean;
  onToggleExpand: (jobOrderId: string) => void;
  intakeOpen: boolean;
  onToggleIntake: (jobOrderId: string) => void;
  onIntakeUploaded: (jobOrderId: string, batchId: string) => void;
  // Selection
  selectionJoId: string | null;
  selectedIds: string[];
  onToggleSelect: (jobOrderId: string, pipelineEntryId: string) => void;
  onSelectAll: (jobOrderId: string, pipelineEntryIds: string[], checked: boolean) => void;
  onClearSelection: () => void;
  onActionDone: (actedIds: string[]) => void;
  // Modals
  onOpenTrail: (pipelineEntryId: string, candidateName: string | null | undefined, candidateTitle: string | null | undefined) => void;
  onRescored: () => void;
  // Filtering
  isFiltering: boolean;
}

const jobOrderHeading = (
  title: string | null | undefined,
  city: string | null | undefined,
  stateCode: string | null | undefined
): string => {
  const location = [city, stateCode].filter(Boolean).join(', ');
  const base = title ?? 'Untitled Job Order';
  return location ? `${base} - ${location}` : base;
};

const statusSummary = (counts: StatusCounts, total: number): string => {
  const parts: string[] = [`${total} applicant${total !== 1 ? 's' : ''}`];
  if (counts.newCount > 0) parts.push(`${counts.newCount} New`);
  if (counts.sendOutreach > 0) parts.push(`${counts.sendOutreach} Send outreach`);
  if (counts.needsHuman > 0) parts.push(`${counts.needsHuman} Needs human`);
  if (counts.assigned > 0) parts.push(`${counts.assigned} Assigned`);
  if (counts.declined > 0) parts.push(`${counts.declined} Declined`);
  return parts.join(' · ');
};

const JobOrderBlock: FC<Props> = ({
  companyId,
  data,
  isExpanded,
  onToggleExpand,
  intakeOpen,
  onToggleIntake,
  onIntakeUploaded,
  selectionJoId,
  selectedIds,
  onToggleSelect,
  onSelectAll,
  onClearSelection,
  onActionDone,
  onOpenTrail,
  onRescored,
  isFiltering,
}) => {
  const heading = jobOrderHeading(data.jobTitle, data.city, data.stateCode);
  const isActiveSelection = selectionJoId === data.jobOrderId;
  const visibleApplicants = data.applicants;
  const visibleIds = visibleApplicants.map((a) => a.pipelineEntryId);
  // Fix B: restrict the selection to ids that are currently rendered in this JO.
  // Stale/declined ids that remain in selectedIds during the cache-and-network
  // refetch window are dropped here so they cannot reach the mutation.
  const effectiveSelectedIds = intersectWithVisible(selectedIds, visibleIds);
  const allSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const someSelected = !allSelected && visibleIds.some((id) => selectedIds.includes(id));

  // When filtering and no matches, hide the block entirely
  if (isFiltering && data.matchCount === 0) {
    return null;
  }

  return (
    <div className="ai-console__jo-block">
      {/* Header row */}
      <div
        className="ai-console__jo-header"
        role="button"
        tabIndex={0}
        onClick={() => onToggleExpand(data.jobOrderId)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggleExpand(data.jobOrderId);
          }
        }}
      >
        <span className={`ai-console__caret ${isExpanded ? 'ai-console__caret--open' : ''}`}>
          &#9654;
        </span>
        <span className="ai-console__jo-title">{heading}</span>
        {isFiltering && data.matchCount > 0 && (
          <Badge color="primary" className="ai-console__match-badge ml-2">
            {data.matchCount} match{data.matchCount !== 1 ? 'es' : ''}
          </Badge>
        )}
        <span className="ai-console__jo-summary text-muted ml-2">
          {statusSummary(data.statusCounts, data.applicantCount)}
        </span>
        <Button
          size="sm"
          color="link"
          className="ai-console__add-resumes ml-auto"
          onClick={(e) => {
            e.stopPropagation();
            onToggleIntake(data.jobOrderId);
          }}
        >
          + Add resumes
        </Button>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="ai-console__jo-body">
          {intakeOpen && (
            <IntakeDropzone
              companyId={companyId}
              jobOrderId={data.jobOrderId}
              onClose={() => onToggleIntake(data.jobOrderId)}
              onUploaded={(batchId) => onIntakeUploaded(data.jobOrderId, batchId)}
            />
          )}

          {isActiveSelection && effectiveSelectedIds.length > 0 && (
            <BulkActionBar
              jobOrderId={data.jobOrderId}
              selectedIds={effectiveSelectedIds}
              onCleared={onClearSelection}
              onActionDone={onActionDone}
            />
          )}

          {visibleApplicants.length === 0 ? (
            <div className="ai-console__no-applicants text-muted">
              No applicants in this job order.
            </div>
          ) : (
            <>
              <ApplicantRowHeader
                allSelected={allSelected}
                someSelected={someSelected}
                onSelectAll={(checked) => onSelectAll(data.jobOrderId, visibleIds, checked)}
              />

              {visibleApplicants.map((row) => (
                <ApplicantRow
                  key={row.pipelineEntryId}
                  row={row}
                  selected={isActiveSelection && selectedIds.includes(row.pipelineEntryId)}
                  onToggleSelect={(pid) => onToggleSelect(data.jobOrderId, pid)}
                  onOpenTrail={onOpenTrail}
                  onRescored={onRescored}
                />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default JobOrderBlock;
